/**
 * AI Sourcing Tab - Dedicated tab for managing AI sourcing across all open jobs
 * Shows jobs list on left, AI-ranked candidates on right
 */
import React, { useState, useEffect } from 'react';
import { Sparkles, Briefcase, MapPin, Calendar, Users, Search, RefreshCw, AlertCircle, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import CandidateMatchCard from '../components/ai-sourcing/CandidateMatchCard';
import AISourcingPanel from '../components/ai-sourcing/AISourcingPanel';
import CandidateDetailModal from '../components/recruiting/CandidateDetailModal';
import CandidateFormComprehensive from '../components/CandidateFormComprehensive';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AISourcingTab = ({ selectedUser, myTeam, teamMembers, onRefresh }) => {
  const [validJobs, setValidJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [idealProfile, setIdealProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [showSourcingPanel, setShowSourcingPanel] = useState(false);
  
  // View Submissions functionality
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [jobSubmissions, setJobSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [submissionsCount, setSubmissionsCount] = useState(0);
  
  // Candidate detail modal
  const [selectedCandidateForModal, setSelectedCandidateForModal] = useState(null);
  
  // Edit candidate modal
  const [showEditCandidateModal, setShowEditCandidateModal] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [candidateModalOpen, setCandidateModalOpen] = useState(false);
  
  // Matching criteria modal
  const [showMatchingCriteria, setShowMatchingCriteria] = useState(false);
  
  // Job search and pagination
  const [jobSearch, setJobSearch] = useState('');
  const [jobPagination, setJobPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    hasMore: false
  });
  const [filteredJobs, setFilteredJobs] = useState([]);
  
  // Filters for candidates
  const [filters, setFilters] = useState({
    tier: null,
    source: null,
    minScore: null,
    skipPipeline: false
  });
  const [candidatePagination, setCandidatePagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    hasMore: false
  });
  // Calculate allowed clients based on user role and team
  // CRITICAL: Regular recruiters INHERIT clients from their team lead
  // See: /app/CRITICAL_RULES.md - Rule #3
  const getAllowedClients = () => {
    if (!selectedUser) return [];

    const isLeadRecruiter = selectedUser.role === 'lead_recruiter' || selectedUser.role === 'lead';
    const isRegularRecruiter = selectedUser.role === 'recruiter';
    
    if (isLeadRecruiter) {
      // Lead Recruiter: Use their own assigned_clients (which covers their whole team)
      const userClients = selectedUser.assigned_clients || [];
      console.log(`[AI Sourcing] Lead Recruiter ${selectedUser.full_name}: ${userClients.length} allowed clients`);
      return userClients;
    } else if (isRegularRecruiter) {
      // Regular Recruiter: Inherit from team lead's assigned_clients
      if (teamMembers && teamMembers.length > 0) {
        const teamLead = teamMembers[0]; // First team member is the lead
        const inheritedClients = teamLead?.assigned_clients || [];
        console.log(`[AI Sourcing] Regular Recruiter ${selectedUser.full_name}: Inherited ${inheritedClients.length} clients from team lead ${teamLead?.full_name}`);
        return inheritedClients;
      } else {
        // Fallback: Use own assigned_clients if no team found
        const userClients = selectedUser.assigned_clients || [];
        console.warn(`[AI Sourcing] Regular Recruiter ${selectedUser.full_name}: No team found, using own ${userClients.length} clients`);
        return userClients;
      }
    } else {
      // Other roles: Use own assigned_clients
      const userClients = selectedUser.assigned_clients || [];
      console.log(`[AI Sourcing] User ${selectedUser.full_name} (${selectedUser.role}): ${userClients.length} allowed clients`);
      return userClients;
    }
  };

  const allowedClients = getAllowedClients();

  useEffect(() => {
    loadValidJobs();
    
    // Check if we're coming from AI Source Candidates with results
    const storedResults = sessionStorage.getItem('aiSourcingResults');
    if (storedResults) {
      try {
        const { jobId, runId, totalCandidates } = JSON.parse(storedResults);
        console.log(`[AI Sourcing Tab] Loading results from Candidates tab: Job ${jobId}, Run ${runId}, Total: ${totalCandidates}`);
        
        // Clear the stored results
        sessionStorage.removeItem('aiSourcingResults');
        
        // The job will be auto-selected in loadValidJobs when jobs load
        // We just need to ensure it's the right job
        if (jobId) {
          // Store jobId to select after jobs load
          sessionStorage.setItem('autoSelectJobId', jobId);
        }
      } catch (error) {
        console.error('Error parsing stored AI sourcing results:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedJob) {
      loadCandidatesForJob(selectedJob.id);
    }
  }, [selectedJob, filters, candidatePagination.page]);

  useEffect(() => {
    // Filter jobs based on search
    if (jobSearch.trim()) {
      const filtered = validJobs.filter(job => 
        job.title && job.title.toLowerCase().includes(jobSearch.toLowerCase())
      );
      setFilteredJobs(filtered);
    } else {
      setFilteredJobs(validJobs);
    }
  }, [jobSearch, validJobs]);

  const loadValidJobs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs?include_sub_jobs=false&limit=1000`);
      if (!response.ok) throw new Error('Failed to load jobs');
      
      const jobs = await response.json();
      
      // Filter for valid sourcing jobs (open status AND has client company)
      const openJobs = jobs.filter(job => 
        job.status === 'accepting_candidates' || 
        job.status === 'open' ||
        job.status === 'active'
      );
      
      const validSourcingJobs = openJobs.filter(job => {
        // Must have a client company assigned
        const hasClientCompany = job.client_company && job.client_company.trim().length > 0;
        if (!hasClientCompany) return false;
        
        // Must be in user's allowed clients (own or team's)
        const isAllowedClient = allowedClients.includes(job.client_company);
        if (!isAllowedClient) return false;
        
        return true;
      });

      // Fetch submission counts for all jobs in parallel
      const jobsWithCounts = await Promise.all(
        validSourcingJobs.map(async (job) => {
          try {
            const response = await fetch(`${BACKEND_URL}/api/submissions/job/${job.id}`);
            if (response.ok) {
              const data = await response.json();
              const aiSubmissionsCount = data.submissions.filter(sub => sub.submission_source === 'ai_suggested').length;
              return {
                ...job,
                days_open: Math.floor((new Date() - new Date(job.created_at)) / (1000 * 60 * 60 * 24)),
                submission_count: aiSubmissionsCount
              };
            }
          } catch (error) {
            console.error(`Error loading submissions for job ${job.id}:`, error);
          }
          return {
            ...job,
            days_open: Math.floor((new Date() - new Date(job.created_at)) / (1000 * 60 * 60 * 24)),
            submission_count: 0
          };
        })
      );
      
      // Calculate filtering stats
      const totalOpen = openJobs.length;
      const withClient = openJobs.filter(j => j.client_company).length;
      const withAllowedClient = validSourcingJobs.length;
      
      const filteredNoClient = totalOpen - withClient;
      const filteredNotAllowed = withClient - withAllowedClient;
      
      console.log(`[AI Sourcing] Job Filtering Results:
        - Total open jobs: ${totalOpen}
        - Jobs WITH client_company: ${withClient}
        - Jobs in allowed clients: ${withAllowedClient}
        - Filtered (no client): ${filteredNoClient}
        - Filtered (not in assigned clients): ${filteredNotAllowed}
      `);
      
      if (filteredNoClient > 0) {
        console.warn(`[AI Sourcing] ${filteredNoClient} jobs filtered out (no client_company)`);
      }
      if (filteredNotAllowed > 0) {
        console.warn(`[AI Sourcing] ${filteredNotAllowed} jobs filtered out (not in assigned clients: ${allowedClients.join(', ')})`);
      }
      
      setValidJobs(jobsWithCounts);
      setJobPagination(prev => ({
        ...prev,
        totalCount: jobsWithCounts.length
      }));
      
      // Check if we need to auto-select a specific job (from Candidates tab redirect)
      const autoSelectJobId = sessionStorage.getItem('autoSelectJobId');
      if (autoSelectJobId) {
        const jobToSelect = jobsWithCounts.find(j => j.id === autoSelectJobId);
        if (jobToSelect) {
          setSelectedJob(jobToSelect);
          console.log(`[AI Sourcing Tab] Auto-selected job: ${jobToSelect.title}`);
        }
        sessionStorage.removeItem('autoSelectJobId');
      } else if (jobsWithCounts.length > 0 && !selectedJob) {
        // Auto-select first job if no specific job requested
        setSelectedJob(jobsWithCounts[0]);
      }
    } catch (error) {
      console.error('Error loading valid jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissionsForJob = async (jobId) => {
    setLoadingSubmissions(true);
    setShowSubmissions(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/submissions/job/${jobId}`);
      if (!response.ok) throw new Error('Failed to load submissions');
      
      const data = await response.json();
      // Filter for AI-sourced submissions only
      const aiSubmissions = data.submissions.filter(sub => sub.submission_source === 'ai_suggested');
      setJobSubmissions(aiSubmissions);
      
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast.error('Failed to load submissions');
      setJobSubmissions([]);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  // Load submissions count for display
  useEffect(() => {
    if (selectedJob) {
      const loadSubmissionsCount = async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/submissions/job/${selectedJob.id}`);
          if (response.ok) {
            const data = await response.json();
            const aiSubmissionsCount = data.submissions.filter(sub => sub.submission_source === 'ai_suggested').length;
            setSubmissionsCount(aiSubmissionsCount);
          }
        } catch (error) {
          console.error('Error loading submissions count:', error);
        }
      };
      loadSubmissionsCount();
    }
  }, [selectedJob]);

  const loadCandidatesForJob = async (jobId) => {
    setLoadingCandidates(true);
    try {
      const params = new URLSearchParams({
        page: candidatePagination.page,
        page_size: candidatePagination.pageSize
      });
      
      if (filters.tier) params.append('tier_filter', filters.tier);
      if (filters.source) params.append('source_filter', filters.source);
      if (filters.minScore) params.append('min_score', filters.minScore);
      if (filters.skipPipeline) params.append('skip_already_in_pipeline', 'true');

      const response = await fetch(`${BACKEND_URL}/api/ai-sourcing/jobs/${jobId}/results?${params}`);
      
      if (response.status === 404) {
        // No results yet for this job
        setCandidates([]);
        setIdealProfile(null);
        return;
      }
      
      if (!response.ok) throw new Error('Failed to load candidates');
      
      const data = await response.json();
      setCandidates(data.candidates);
      setIdealProfile(data.ideal_profile);
      setCandidatePagination(prev => ({
        ...prev,
        totalCount: data.total_count,
        hasMore: data.has_more
      }));
    } catch (error) {
      console.error('Error loading candidates:', error);
      console.log(`Failed to load candidates for job ${jobId}:`, error.message);
      setCandidates([]);
      setIdealProfile(null);
      // Don't show toast error for 404s as that's expected for new jobs
      if (!error.message.includes('404') && !error.message.includes('No results')) {
        toast.error('Failed to load AI sourcing results');
      }
    } finally {
      setLoadingCandidates(false);
    }
  };

  const runSourcingForJob = (job) => {
    setSelectedJob(job);
    setShowSourcingPanel(true);
  };

  const handleAddToPipeline = async (match) => {
    try {
      console.log('═══════════════════════════════════════════════════');
      console.log('[AI Sourcing] STARTING AI SOURCING SUBMISSION');
      console.log('═══════════════════════════════════════════════════');
      console.log('[AI Sourcing] Step 1: Preparing submission data');
      console.log('[AI Sourcing] - Job ID:', selectedJob.id);
      console.log('[AI Sourcing] - Job Title:', selectedJob.title);
      console.log('[AI Sourcing] - Candidate ID:', match.candidate_id);
      console.log('[AI Sourcing] - Candidate Name:', match.candidate_name);
      console.log('[AI Sourcing] - Recruiter ID:', selectedUser?.id);
      console.log('[AI Sourcing] - Recruiter Name:', selectedUser?.full_name || selectedUser?.name);
      console.log('[AI Sourcing] - Match Score:', match.match_score?.overall_score || 0);
      
      const response = await fetch(`${BACKEND_URL}/api/ai-sourcing/jobs/${selectedJob.id}/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_id: selectedJob.id,
          candidate_id: match.candidate_id,
          match_id: match.id,
          notes: `AI Sourced - ${match.match_score?.overall_score || 0}% match`,
          recruiter_id: selectedUser?.id,
          recruiter_name: selectedUser?.full_name || selectedUser?.name
        })
      });

      console.log('[AI Sourcing] Step 2: Backend API call completed');
      console.log('[AI Sourcing] - Response Status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[AI Sourcing] ERROR: Backend returned error:', errorData);
        throw new Error(errorData.detail || 'Failed to add to pipeline');
      }

      const data = await response.json();
      console.log('[AI Sourcing] Step 3: Submission created successfully');
      console.log('[AI Sourcing] - Submission ID:', data.submission_id);
      console.log('[AI Sourcing] - Already Existed:', data.already_existed);
      
      // Show success message
      toast.success('Successfully added candidate to job', {
        duration: 4000
      });
      
      // Update the match in candidates list to show "In Pipeline" status
      console.log('[AI Sourcing] Step 4: Updating local candidates list');
      setCandidates(prevCandidates => 
        prevCandidates.map(c => 
          c.id === match.id 
            ? { ...c, already_in_pipeline: true, submission_id: data.submission_id }
            : c
        )
      );
      
      // Refresh dashboard data (submissions and candidates) to update Overview tab
      console.log('[AI Sourcing] Step 5: Triggering dashboard refresh via onRefresh callback');
      console.log('[AI Sourcing] - onRefresh exists?', !!onRefresh);
      if (onRefresh) {
        console.log('[AI Sourcing] - Calling onRefresh() now...');
        const refreshStart = Date.now();
        await onRefresh();
        const refreshDuration = Date.now() - refreshStart;
        console.log('[AI Sourcing] - Dashboard refresh complete (took', refreshDuration, 'ms)');
      } else {
        console.warn('[AI Sourcing] - WARNING: onRefresh callback not provided!');
      }
      
      // Refresh submissions count
      console.log('[AI Sourcing] Step 6: Refreshing submission count for job');
      const loadSubmissionsCount = async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/submissions/job/${selectedJob.id}`);
          if (response.ok) {
            const data = await response.json();
            const aiSubmissionsCount = data.submissions.filter(sub => sub.submission_source === 'ai_suggested').length;
            console.log('[AI Sourcing] - Updated submission count:', aiSubmissionsCount);
            setSubmissionsCount(aiSubmissionsCount);
          }
        } catch (error) {
          console.error('[AI Sourcing] Error loading submissions count:', error);
        }
      };
      await loadSubmissionsCount();
      
      console.log('═══════════════════════════════════════════════════');
      console.log('[AI Sourcing] AI SOURCING SUBMISSION COMPLETE');
      console.log('═══════════════════════════════════════════════════');
      
    } catch (error) {
      console.error('[AI Sourcing] FATAL ERROR:', error);
      console.error('[AI Sourcing] Stack trace:', error.stack);
      toast.error(error.message || 'Failed to add candidate to pipeline');
    }
  };

  const handleViewProfile = async (match) => {
    try {
      // Fetch full candidate details from API
      const response = await fetch(`${BACKEND_URL}/api/candidates/${match.candidate_id}`);
      if (response.ok) {
        const candidateData = await response.json();
        setSelectedCandidateForModal(candidateData);
        setCandidateModalOpen(true);
      } else {
        // If candidate doesn't exist in main database, create a pseudo-candidate object from AI match data
        const pseudoCandidate = {
          id: match.candidate_id,
          name: match.candidate_name,
          email: match.email || '',
          phone: match.phone || '',
          location: match.location || '',
          current_title: match.current_title || '',
          current_company: match.current_company || '',
          experience_years: match.experience_years || 0,
          skills: match.skills || [],
          source: 'ai_sourcing',
          ai_match_score: match.match_score?.overall_score || 0,
          ai_match_reason: match.match_reason || '',
          already_in_pipeline: match.already_in_pipeline || false
        };
        setSelectedCandidateForModal(pseudoCandidate);
        setCandidateModalOpen(true);
      }
    } catch (error) {
      console.error('Error loading candidate details:', error);
      toast.error('Failed to load candidate details');
    }
  };

  const handleEditCandidate = async (candidate) => {
    // Close the detail modal first
    setCandidateModalOpen(false);
    
    // Set the candidate for editing
    setEditingCandidate(candidate);
    setShowEditCandidateModal(true);
  };

  const handleFeedback = async (match, feedbackType) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-sourcing/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          match_id: match.id,
          job_id: selectedJob.id,
          candidate_id: match.candidate_id,
          feedback_type: feedbackType
        })
      });

      if (!response.ok) throw new Error('Failed to submit feedback');

      toast.success('Feedback recorded');
      loadCandidatesForJob(selectedJob.id);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback');
    }
  };

  // Jobs pagination helpers
  const getCurrentJobsPage = () => {
    const startIndex = (jobPagination.page - 1) * jobPagination.pageSize;
    const endIndex = startIndex + jobPagination.pageSize;
    return filteredJobs.slice(startIndex, endIndex);
  };

  const getTotalJobPages = () => {
    return Math.ceil(filteredJobs.length / jobPagination.pageSize);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading AI Sourcing...</p>
        </div>
      </div>
    );
  }

  if (validJobs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Open Jobs Available</h3>
          <p className="text-gray-600 mb-4">
            AI Sourcing is only available for jobs that are open and accepting candidates.
            Please create a new job or ensure existing jobs are not closed or filled.
          </p>
          <button className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
            Create New Job
          </button>
        </div>
      </div>
    );
  }

  const currentJobsPage = getCurrentJobsPage();
  const totalJobPages = getTotalJobPages();

  return (
    <div className="h-full flex">
      {/* Left Pane - Jobs List with Search & Pagination */}
      <div className="w-80 border-r bg-gray-50 overflow-y-auto flex flex-col">
        <div className="p-4 border-b bg-white sticky top-0 z-10">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-3">
            <Briefcase className="w-5 h-5" />
            Open Jobs ({validJobs.length})
          </h2>
          
          {/* Search Box */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search jobs by title..."
              value={jobSearch}
              onChange={(e) => setJobSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
            />
          </div>
          
          <p className="text-sm text-gray-600">
            Showing {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Jobs List */}
        <div className="flex-1 p-2 space-y-2">
          {currentJobsPage.map((job) => (
            <button
              key={job.id}
              onClick={() => setSelectedJob(job)}
              className={`w-full text-left p-3 rounded-lg transition ${
                selectedJob?.id === job.id
                  ? 'bg-purple-100 border-2 border-purple-600'
                  : 'bg-white border border-gray-200 hover:border-purple-300'
              }`}
            >
              <h3 className="font-medium mb-1 text-sm">{job.title}</h3>
              <div className="text-xs text-gray-600 space-y-1">
                {job.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {job.location}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {job.days_open} days open
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {job.submission_count} submissions
                </div>
              </div>
              {job.last_sourcing_run && (
                <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Last sourced: {new Date(job.last_sourcing_run).toLocaleDateString()}
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Jobs Pagination */}
        {totalJobPages > 1 && (
          <div className="p-3 border-t bg-white">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                Page {jobPagination.page} of {totalJobPages}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setJobPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                  disabled={jobPagination.page === 1}
                  className="p-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setJobPagination(p => ({ ...p, page: Math.min(totalJobPages, p.page + 1) }))}
                  disabled={jobPagination.page === totalJobPages}
                  className="p-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right Pane - AI Candidates */}
      <div className="flex-1 overflow-y-auto">
        {selectedJob ? (
          <div>
            {/* Header with Job Info */}
            <div className="sticky top-0 bg-white border-b z-10">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-2xl font-bold mb-1">{selectedJob.title}</h2>
                    <p className="text-gray-600">{selectedJob.location || 'Location not specified'}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => loadSubmissionsForJob(selectedJob.id)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <Users className="w-4 h-4" />
                      View Submissions ({submissionsCount})
                    </button>
                    <button
                      onClick={() => runSourcingForJob(selectedJob)}
                      className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 flex items-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      {candidates.length > 0 ? 'Refresh AI Matches' : 'Start AI Sourcing'}
                    </button>
                    <button
                      onClick={() => setShowMatchingCriteria(true)}
                      className="px-4 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 flex items-center gap-2"
                      title="View matching criteria"
                    >
                      <Info className="w-4 h-4" />
                      Matching Criteria
                    </button>
                  </div>
                </div>

                {/* Ideal Profile */}
                {idealProfile && (
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4 mb-4">
                    <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      AI-Generated Ideal Candidate Profile
                    </h3>
                    <p className="text-sm text-purple-800">{idealProfile.summary}</p>
                  </div>
                )}

                {/* Submissions Panel */}
                {showSubmissions && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        AI-Sourced Submissions for this Job ({jobSubmissions.length})
                      </h3>
                      <button
                        onClick={() => setShowSubmissions(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ×
                      </button>
                    </div>
                    
                    {loadingSubmissions ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        <span className="ml-2 text-gray-600">Loading submissions...</span>
                      </div>
                    ) : jobSubmissions.length > 0 ? (
                      <div className="space-y-2">
                        {jobSubmissions.map((submission) => (
                          <div key={submission.id} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0">
                                <Sparkles className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{submission.candidate_name}</p>
                                <p className="text-sm text-gray-600">
                                  Status: {submission.current_status} • 
                                  Added: {new Date(submission.submission_date || submission.created_at).toLocaleDateString()} •
                                  Match: {submission.match_score || 0}%
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                                AI Sourced
                              </span>
                              <button
                                onClick={() => window.open(`#/submissions/${submission.id}`, '_blank')}
                                className="text-blue-600 hover:text-blue-700 text-sm"
                              >
                                View →
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-gray-500">
                        <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p>No candidates have been submitted yet.</p>
                        <p className="text-sm">Use "Submit to Job" below to add candidates.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Filters */}
              <div className="px-6 pb-4 flex gap-2 flex-wrap">
                <select
                  value={filters.tier || ''}
                  onChange={(e) => setFilters({ ...filters, tier: e.target.value || null })}
                  className="px-3 py-1 border rounded-lg text-sm"
                >
                  <option value="">All Tiers</option>
                  <option value="strong">Strong Match</option>
                  <option value="medium">Medium Match</option>
                  <option value="low">Low Match</option>
                </select>

                <select
                  value={filters.source || ''}
                  onChange={(e) => setFilters({ ...filters, source: e.target.value || null })}
                  className="px-3 py-1 border rounded-lg text-sm"
                >
                  <option value="">All Sources</option>
                  <option value="internal">Internal</option>
                  <option value="job_board_a">Job Board A</option>
                  <option value="network_b">Network B</option>
                </select>

                <input
                  type="number"
                  placeholder="Min Score"
                  value={filters.minScore || ''}
                  onChange={(e) => setFilters({ ...filters, minScore: parseInt(e.target.value) || null })}
                  className="px-3 py-1 border rounded-lg text-sm w-32"
                  min="0"
                  max="100"
                />

                <label className="flex items-center gap-2 px-3 py-1 border rounded-lg text-sm cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={filters.skipPipeline}
                    onChange={(e) => setFilters({ ...filters, skipPipeline: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span>Hide already in pipeline</span>
                </label>
              </div>
            </div>

            {/* Candidates List */}
            <div className="p-6">
              {loadingCandidates ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading AI matches...</p>
                  </div>
                </div>
              ) : candidates.length === 0 ? (
                <div className="text-center py-12">
                  <Sparkles className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No AI Matches Yet</h3>
                  <p className="text-gray-600 mb-4">
                    Click "Start AI Sourcing" to find matching candidates for this job.
                  </p>
                  <button
                    onClick={() => runSourcingForJob(selectedJob)}
                    className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700"
                  >
                    Start AI Sourcing
                  </button>
                </div>
              ) : (
                <div>
                  <div className="mb-4 flex justify-between items-center">
                    <p className="text-sm text-gray-600">
                      Showing {candidates.length} of {candidatePagination.totalCount} matches
                    </p>
                  </div>

                  <div className="space-y-4">
                    {candidates.map((match) => (
                      <CandidateMatchCard
                        key={match.id}
                        match={match}
                        onAddToPipeline={handleAddToPipeline}
                        onViewProfile={handleViewProfile}
                        onFeedback={handleFeedback}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {candidatePagination.hasMore && (
                    <div className="mt-6 text-center">
                      <button
                        onClick={() => setCandidatePagination(p => ({ ...p, page: p.page + 1 }))}
                        className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Load More
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">Select a job to view AI sourcing results</p>
          </div>
        )}
      </div>

      {/* AI Sourcing Panel Modal */}
      {showSourcingPanel && selectedJob && (
        <AISourcingPanel
          job={selectedJob}
          onClose={() => setShowSourcingPanel(false)}
          onCandidatesFound={(runId) => {
            setShowSourcingPanel(false);
            // Immediately reload candidates to show new results
            loadCandidatesForJob(selectedJob.id);
            // Also refresh submissions count
            const loadSubmissionsCount = async () => {
              try {
                const response = await fetch(`${BACKEND_URL}/api/submissions/job/${selectedJob.id}`);
                if (response.ok) {
                  const data = await response.json();
                  const aiSubmissionsCount = data.submissions.filter(sub => sub.submission_source === 'ai_suggested').length;
                  setSubmissionsCount(aiSubmissionsCount);
                }
              } catch (error) {
                console.error('Error loading submissions count:', error);
              }
            };
            loadSubmissionsCount();
          }}
        />
      )}
      {/* Candidate Detail Modal */}
      {selectedCandidateForModal && (
        <CandidateDetailModal
          candidate={selectedCandidateForModal}
          isOpen={candidateModalOpen}
          onClose={() => {
            setCandidateModalOpen(false);
            setSelectedCandidateForModal(null);
          }}
          onEdit={handleEditCandidate}
          submissions={[]} // We can load submissions if needed
        />
      )}

      {/* Edit Candidate Modal */}
      {showEditCandidateModal && editingCandidate && (
        <CandidateFormComprehensive
          candidate={editingCandidate}
          onClose={() => {
            setShowEditCandidateModal(false);
            setEditingCandidate(null);
          }}
          onSuccess={() => {
            setShowEditCandidateModal(false);
            setEditingCandidate(null);
            toast.success('Candidate updated successfully');
            // Optionally refresh candidates if needed
            if (selectedJob) {
              runSourcingForJob(selectedJob);
            }
          }}
        />
      )}

      {/* Matching Criteria Modal */}
      {showMatchingCriteria && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-t-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Sparkles className="w-6 h-6" />
                    AI Matching Criteria
                  </h2>
                  <p className="text-purple-100 mt-1">How candidates are scored and ranked</p>
                </div>
                <button
                  onClick={() => setShowMatchingCriteria(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Overview */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Scoring Overview</h3>
                <p className="text-sm text-blue-800">
                  Our AI evaluates candidates across five key dimensions, with weighted scoring to identify the best matches for your role.
                </p>
              </div>

              {/* Criteria List */}
              <div className="space-y-4">
                {/* Skills */}
                <div className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">1. Skills Match</h4>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">40%</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Evaluates alignment between candidate skills and job requirements
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 pl-4">
                    <li>• Must-have skills (higher weight)</li>
                    <li>• Good-to-have skills (bonus points)</li>
                    <li>• Technology stack proficiency</li>
                  </ul>
                </div>

                {/* Experience */}
                <div className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">2. Experience Level</h4>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">25%</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Matches years of experience with role requirements
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 pl-4">
                    <li>• Minimum experience threshold</li>
                    <li>• Maximum experience range</li>
                    <li>• Seniority level alignment</li>
                  </ul>
                </div>

                {/* Location */}
                <div className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">3. Location Match</h4>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">15%</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Evaluates geographic alignment and work arrangement preferences
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 pl-4">
                    <li>• Preferred locations</li>
                    <li>• Remote, hybrid, or on-site compatibility</li>
                    <li>• Relocation willingness</li>
                  </ul>
                </div>

                {/* Domain */}
                <div className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">4. Domain Relevance</h4>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-semibold">15%</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Assesses industry and role type similarity
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 pl-4">
                    <li>• Current job title relevance</li>
                    <li>• Industry background</li>
                    <li>• Company type experience</li>
                  </ul>
                </div>

                {/* Availability */}
                <div className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">5. Availability</h4>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-semibold">5%</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Considers candidate availability and notice period
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 pl-4">
                    <li>• Current employment status</li>
                    <li>• Notice period requirements</li>
                    <li>• Start date flexibility</li>
                  </ul>
                </div>
              </div>

              {/* Final Score Explanation */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-semibold text-purple-900 mb-2">Final Match Score</h3>
                <p className="text-sm text-purple-800 mb-3">
                  The overall score (0-100) is calculated by combining all criteria with their respective weights.
                </p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-gray-700"><strong>85-100:</strong> Excellent Match</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-gray-700"><strong>70-84:</strong> Strong Match</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <span className="text-gray-700"><strong>55-69:</strong> Good Fit</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                    <span className="text-gray-700"><strong>0-54:</strong> Potential Fit</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-lg border-t">
              <button
                onClick={() => setShowMatchingCriteria(false)}
                className="w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 font-medium"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AISourcingTab;
